import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Server, CheckCircle2, XCircle, AlertTriangle,
  Wrench, Eye, Loader2, Activity, Zap, TrendingUp, Clock,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const statusConfig = {
  active:      { icon: CheckCircle2, color: "#34d399", bg: "rgba(52,211,153,0.1)",  border: "rgba(52,211,153,0.25)",  dot: "#34d399",  label: "active" },
  inactive:    { icon: XCircle,      color: "#64748b", bg: "rgba(100,116,139,0.1)", border: "rgba(100,116,139,0.2)",  dot: "#475569",  label: "inactive" },
  maintenance: { icon: Wrench,       color: "#fbbf24", bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.25)",  dot: "#fbbf24",  label: "maintenance" },
  degraded:    { icon: AlertTriangle,color: "#f97316", bg: "rgba(249,115,22,0.1)",  border: "rgba(249,115,22,0.25)",  dot: "#f97316",  label: "degraded" },
};

const typeConfig = {
  bank:    { gradient: "#22d3ee, #0284c7", accent: "#22d3ee", label: "Bank" },
  ewallet: { gradient: "#a78bfa, #7c3aed", accent: "#a78bfa", label: "E-Wallet" },
  qr:      { gradient: "#34d399, #059669", accent: "#34d399", label: "QR Code" },
  card:    { gradient: "#fb923c, #ea580c", accent: "#fb923c", label: "Card" },
};

/* ─── Premium Stat Card ─────────────────────────────────────── */
function StatCard({ title, value, icon: Icon, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 22, scale: 0.93 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 900 }}
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.08 : 0.85 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1e, transparent 68%)`, filter: "blur(10px)" }}
      />
      <motion.div
        animate={{ y: hovered ? -8 : 0, rotateX: hovered ? -2 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
          border: `1px solid ${hovered ? `${accent}38` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 20px 55px rgba(0,0,0,0.52), 0 0 30px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }} />
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.15, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}0a, transparent 70%)` }} />
        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100 leading-none">{value}</motion.p>
          </div>
          <motion.div
            animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${gradient})`, boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`, transition: "box-shadow 0.3s" }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Provider Card ─────────────────────────────────────────── */
function ProviderCard({ provider, language, t, index }) {
  const [hovered, setHovered] = useState(false);
  const statusKey = (provider.status || "").toLowerCase();
  const st = statusConfig[statusKey] || statusConfig.inactive;
  const type = typeConfig[provider.type] || typeConfig.bank;
  const StatusIcon = st.icon;
  const successRate = provider.success_rate ?? 100;
  const responseTime = provider.avg_response_time ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -16, scale: 0.94 }}
      transition={{ delay: index * 0.04, type: "spring", stiffness: 220, damping: 22 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 1000 }}
    >
      {/* Outer radial glow */}
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.06 : 0.88 }}
        transition={{ duration: 0.35 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 30%, ${type.accent}22, transparent 65%)`, filter: "blur(12px)" }}
      />

      {/* Card body */}
      <motion.div
        animate={{
          y: hovered ? -10 : 0,
          rotateX: hovered ? -2.5 : 0,
          rotateY: hovered ? 1 : 0,
        }}
        transition={{ type: "spring", stiffness: 340, damping: 24 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.88), rgba(8,14,30,0.93))",
          border: `1px solid ${hovered ? `${type.accent}40` : "rgba(148,163,184,0.09)"}`,
          boxShadow: hovered
            ? `0 24px 64px rgba(0,0,0,0.6), 0 0 40px ${type.accent}18, inset 0 1px 0 rgba(255,255,255,0.04)`
            : "0 4px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.02)",
          transition: "border-color 0.3s, box-shadow 0.3s",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Top shimmer */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${type.accent}60, transparent)` }} />

        {/* Bottom accent sweep on hover */}
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${type.accent}, transparent)` }}
        />

        {/* BG mesh orb */}
        <div className="absolute top-0 right-0 w-36 h-36 pointer-events-none overflow-hidden">
          <div style={{ position: "absolute", top: -18, right: -18, width: 80, height: 80, borderRadius: "50%", background: `${type.accent}0c`, filter: "blur(16px)" }} />
        </div>

        {/* Shimmer sweep on hover */}
        <motion.div
          animate={{ x: hovered ? "115%" : "-115%" }}
          transition={{ duration: 0.75, ease: "easeInOut" }}
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(90deg, transparent, ${type.accent}08, transparent)` }}
        />

        <div className="p-5">
          {/* Top row: icon + name + status dot */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <motion.div
                animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 8 : 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${type.gradient})`,
                  boxShadow: hovered ? `0 0 24px ${type.accent}55` : `0 4px 16px ${type.accent}30`,
                  transition: "box-shadow 0.3s",
                }}
              >
                <Server className="h-6 w-6 text-white" />
              </motion.div>

              <div className="flex-1 min-w-0">
                <motion.h3
                  animate={{ x: hovered ? 2 : 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 26 }}
                  className="font-bold text-slate-100 truncate leading-tight text-sm"
                >
                  {getProviderName(provider, language)}
                </motion.h3>
                <p className="text-xs font-mono text-slate-500 mt-0.5 truncate">{provider.code}</p>
              </div>
            </div>

            {/* Animated status dot */}
            <motion.div
              animate={{ scale: statusKey === "active" ? [1, 1.5, 1] : 1, opacity: statusKey === "active" ? [1, 0.6, 1] : 0.6 }}
              transition={{ repeat: statusKey === "active" ? Infinity : 0, duration: 2.2, ease: "easeInOut" }}
              className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
              style={{ background: st.dot, boxShadow: `0 0 8px ${st.dot}` }}
            />
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {/* Type */}
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-lg uppercase tracking-wide"
              style={{ background: `${type.accent}14`, border: `1px solid ${type.accent}30`, color: type.accent }}
            >
              {type.label}
            </span>
            {/* Status */}
            <motion.div
              animate={{ scale: hovered ? 1.04 : 1 }}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-lg uppercase tracking-wide"
              style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}
            >
              <StatusIcon className="h-2.5 w-2.5" />
              {t(st.label)}
            </motion.div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {/* Success Rate */}
            <div
              className="p-3 rounded-xl relative overflow-hidden"
              style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.07)" }}
            >
              {/* Mini progress bar */}
              <div className="absolute bottom-0 left-0 h-[2px] rounded-full" style={{ width: `${successRate}%`, background: successRate >= 90 ? "#34d399" : successRate >= 70 ? "#fbbf24" : "#f87171", transition: "width 0.6s" }} />
              <p className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">{t("successRate")}</p>
              <motion.p
                animate={{ scale: hovered ? 1.06 : 1 }}
                className="font-bold text-sm"
                style={{ color: successRate >= 90 ? "#34d399" : successRate >= 70 ? "#fbbf24" : "#f87171" }}
              >
                {successRate}%
              </motion.p>
            </div>

            {/* Response Time */}
            <div
              className="p-3 rounded-xl"
              style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.07)" }}
            >
              <p className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">{t("response")}</p>
              <motion.p animate={{ scale: hovered ? 1.06 : 1 }} className="font-bold text-sm text-slate-200">
                {responseTime}ms
              </motion.p>
            </div>
          </div>

          {/* Capability badges */}
          <div className="flex flex-wrap gap-1.5">
            {provider.supports_deposit && (
              <motion.span
                animate={{ y: hovered ? -1 : 0 }}
                transition={{ delay: 0.05 }}
                className="text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                style={{ background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.22)", color: "#34d399" }}
              >
                {t("deposit")}
              </motion.span>
            )}
            {provider.supports_withdrawal && (
              <motion.span
                animate={{ y: hovered ? -1 : 0 }}
                transition={{ delay: 0.08 }}
                className="text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                style={{ background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.22)", color: "#a78bfa" }}
              >
                {t("withdrawal")}
              </motion.span>
            )}
            {provider.supports_qr && (
              <motion.span
                animate={{ y: hovered ? -1 : 0 }}
                transition={{ delay: 0.11 }}
                className="text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.22)", color: "#22d3ee" }}
              >
                QR
              </motion.span>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineProviders() {
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const resellerName = "Gaming Partners Ltd";
  const { language, t } = useLanguage();

  const translateStatus = (status) => {
    const map = { active: t("active"), inactive: t("inactive"), degraded: t("degraded"), maintenance: t("maintenance") };
    return map[(status || "").toLowerCase()] ?? status;
  };

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const filteredProviders = providers.filter(p => {
    const name = getProviderName(p, language);
    return name?.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase());
  });

  const activeProviders = providers.filter(p => p.status === "active").length;
  const avgSuccessRate = Math.round(providers.reduce((s, p) => s + (p.success_rate ?? 100), 0) / (providers.length || 1));

  return (
    <EngineLayout currentPage="EngineProviders" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
            border: "1px solid rgba(99,102,241,0.15)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.5), rgba(34,211,238,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(99,102,241,0.07), transparent 70%)" }} />

          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-indigo-400 mb-1">Payment Infrastructure</p>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">{t("providers")}</h1>
                <motion.div
                  whileHover={{ scale: 1.06 }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                  style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.18)", color: "#22d3ee" }}
                >
                  <Eye className="h-3 w-3" />
                  <span className="text-[10px] font-bold uppercase tracking-wide">{t("viewOnly")}</span>
                </motion.div>
              </div>
              <p className="text-sm text-slate-400">{t("paymentProviderStatus")}</p>
            </div>
          </div>
        </motion.div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title={t("totalProviders")}  value={providers.length}    icon={Server}      gradient="#6366f1, #4f46e5" accent="#818cf8" delay={0} />
          <StatCard title={t("activeProviders")} value={activeProviders}     icon={CheckCircle2} gradient="#34d399, #059669" accent="#34d399" delay={0.05} />
          <StatCard title={t("avgSuccessRate")}  value={`${avgSuccessRate}%`} icon={Activity}    gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0.1} />
        </div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.13 }}
          className="relative max-w-sm"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: searchFocused ? "#818cf8" : "#475569", transition: "color 0.2s" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("searchProviders")}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
            style={{
              background: "rgba(15,23,42,0.7)",
              border: `1px solid ${searchFocused ? "rgba(99,102,241,0.3)" : "rgba(148,163,184,0.1)"}`,
              boxShadow: searchFocused ? "0 0 0 3px rgba(99,102,241,0.07)" : "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
          />
        </motion.div>

        {/* ── Provider Grid ── */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center py-14"
            >
              <div className="flex items-center gap-2.5">
                <Loader2 className="h-5 w-5 text-indigo-400 animate-spin" />
                <span className="text-sm text-slate-500">{t("loading")}</span>
              </div>
            </motion.div>
          ) : filteredProviders.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-14 text-slate-600 text-sm"
            >
              {t("noProviders")}
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filteredProviders.map((provider, i) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  language={language}
                  t={t}
                  index={i}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </EngineLayout>
  );
}